import { ToastQueue } from '@heroui/react'
import { flushSync } from 'react-dom'
import { isIgnorableClientTransitionError } from './clientErrors'

export type QueryErrorMeta = {
  suppressErrorToast?: boolean
}

const recentKeys = new Map<string, number>()
const DEDUPE_MS = 1500
const TOAST_TIMEOUT_MS = 4000

/**
 * App toast queue without document.startViewTransition.
 * HeroUI's default queue wraps adds in View Transitions; when a mutation
 * fails (422) and flips button pending state in the same turn, the VT aborts
 * and Next.js surfaces Runtime InvalidStateError overlays.
 */
export const appToastQueue = new ToastQueue({
  maxVisibleToasts: 4,
  wrapUpdate: (fn) => {
    flushSync(fn)
  },
})

export function errorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (err instanceof Error && err.message.trim()) return err.message.trim()
  if (typeof err === 'string' && err.trim()) return err.trim()
  return fallback
}

function isAbortError(err: unknown): boolean {
  if (isIgnorableClientTransitionError(err)) return true
  if (!err || typeof err !== 'object') return false
  const name = 'name' in err ? String((err as { name?: unknown }).name) : ''
  const message = errorMessage(err, '')
  return name === 'AbortError' || /aborted|abort/i.test(message)
}

function shouldDedupe(key: string): boolean {
  const now = Date.now()
  const last = recentKeys.get(key)
  recentKeys.set(key, now)
  if (recentKeys.size > 50) {
    for (const [k, t] of recentKeys) {
      if (now - t > DEDUPE_MS) recentKeys.delete(k)
    }
  }
  return last !== undefined && now - last < DEDUPE_MS
}

function showToast(
  message: string,
  variant: 'default' | 'accent' | 'success' | 'warning' | 'danger',
): string {
  return appToastQueue.add(
    { title: message, variant },
    { timeout: TOAST_TIMEOUT_MS },
  )
}

/** Show a danger toast for an error. Skips AbortError. Dedupes identical messages briefly. */
export function toastError(err: unknown, fallback = 'Something went wrong'): string | undefined {
  if (isAbortError(err)) return undefined
  const message = errorMessage(err, fallback)
  if (shouldDedupe(message)) return undefined
  return showToast(message, 'danger')
}

export function toastSuccess(message: string): string | undefined {
  if (!message.trim()) return undefined
  if (shouldDedupe(`success:${message}`)) return undefined
  return showToast(message, 'success')
}
